// SOK-34 (INF-07): lo que sale hacia Sentry pasa por redactForSentry. Es la misma regla que kiosk-backend
// (functions/helpers/log_redaction.js, BE-28), más los campos del carro que el tótem maneja.
//
// Dos redes: por nombre de campo (credenciales, datos del comprador, respuestas crudas del proveedor de pago,
// que se quitan enteras porque traen al titular de la tarjeta) y por valor (tokens Bearer/Basic, RUT, email,
// números de tarjeta y la query de las URL) en cualquier texto.

export const REDACTED = "[REDACTED]";

const SENSITIVE_KEY = /authorization|token|secret|passw|api[-_]?key|cookie|credential|private[-_]?key|header[-_]?value|signature|^pin$/i;

// El bloque del comprador y el RUT solo.
const PERSONAL_KEY = /^(customer|fiscal[-_]?id|rut|member)$/i;

// Lo que respondieron Transbank, Getnet y Mercado Pago, como lo guardan el carro y kiosk-backend, y el contacto de una persona.
const REPORT_KEY = /^(details|better_details|summary|checkout_summary|payment_summary|processed_sale|transaction_data|response_data|provider_response|payment_data|receipt_?content|ticket_data|payer|card|cardholder|email|phone|address|first_?name|last_?name|full_?name)$/i;

// "Bearer abc.def" / "Basic abc=" dentro de cualquier texto.
const AUTH_SCHEME = /\b(Bearer|Basic)\s+[A-Za-z0-9\-._~+/]+=*/gi;

// Un RUT con o sin puntos.
const RUT_VALUE = /\b\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]\b/g;

const EMAIL_VALUE = /[^\s@"'<>(),;:]+@[^\s@"'<>(),;:]+\.[A-Za-z]{2,}/g;

// 15 a 19 dígitos, quizás agrupados con espacios o guiones; es una tarjeta si pasan Luhn. Un timestamp en ms tiene 13.
const CARD_CANDIDATE = /\b\d(?:[ -]?\d){14,18}\b/g;

// La query de una URL, que puede traer una llave.
const URL_QUERY = /(\bhttps?:\/\/[^\s?#"'<>]+)\?[^\s#"'<>]*/gi;

const KEY = new RegExp([SENSITIVE_KEY, PERSONAL_KEY, REPORT_KEY].map((pattern) => pattern.source).join("|"), "i");

const MAX_DEPTH = 12;

const passesLuhn = (digits) => {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

const redactCardNumber = (match) => passesLuhn(match.replace(/[ -]/g, "")) ? REDACTED : match;

const PATTERNS = [
  [AUTH_SCHEME, `$1 ${REDACTED}`],
  [URL_QUERY, `$1?${REDACTED}`],
  [RUT_VALUE, REDACTED],
  [EMAIL_VALUE, REDACTED],
  [CARD_CANDIDATE, redactCardNumber],
];

const redactString = (text) => PATTERNS.reduce((result, [pattern, replacement]) => result.replace(pattern, replacement), text);

const redact = (value, seen, depth) => {
  if (typeof value === "string") return redactString(value);
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Date) return value;
  if (depth > MAX_DEPTH) return "[TRUNCATED]";
  if (seen.has(value)) return "[CIRCULAR]";
  seen.add(value);
  if (Array.isArray(value)) {
    const items = value.map((item) => redact(item, seen, depth + 1));
    seen.delete(value);
    return items;
  }

  // Un Error conserva mensaje y stack, que no son propiedades enumerables.
  const source = value instanceof Error ? { name: value.name, message: value.message, stack: value.stack, ...value } : value;

  const result = {};
  for (const [key, item] of Object.entries(source)) {
    result[key] = KEY.test(key) && item !== null && item !== undefined && item !== "" ?
      REDACTED :
      redact(item, seen, depth + 1);
  }
  // Solo se cortan los ciclos: el mismo objeto alcanzado por dos caminos se envía las dos veces.
  seen.delete(value);
  return result;
};

/**
 * Copia de `value` apta para Sentry: credenciales, datos del comprador, números de tarjeta, respuestas crudas del
 * proveedor de pago y query de las URL reemplazados por [REDACTED].
 * @param {*} value - Lo que se va a reportar.
 * @return {*}
 */
export const redactForSentry = (value) => redact(value, new WeakSet(), 0);
